import { ArrowLeft, Maximize2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { Copy, Check } from 'lucide-react';
import { apiFetch } from '../api/client';
import { WaveformViewer, WaveformJumpTo } from './WaveformViewer';

interface PointData {
  V: number;
  H: number;
  A: number;
}

interface Thresholds {
  yellow: number;
  red: number;
}

interface Point {
  id: string;
  name: string;
  velocity: PointData;
  acceleration: PointData;
  envelope: PointData;
  temperature: PointData;
  velocityThresholds?: Thresholds;
  accelerationThresholds?: Thresholds;
  envelopeThresholds?: Thresholds;
  temperatureThresholds?: Thresholds;
  velocityThresholdsByAxis?: Record<string, Thresholds>;
  accelerationThresholdsByAxis?: Record<string, Thresholds>;
  envelopeThresholdsByAxis?: Record<string, Thresholds>;
  status: 'low' | 'medium' | 'high';
  machineId?: string;
  axisIds?: number[];
  axisIdsByLabel?: Record<string, number>;
}

interface PointDetailProps {
  point: Point;
  onBack: () => void;
  onThresholdsUpdate?: (pointId: string, metric: string, thresholds: Thresholds) => void;
  initialAxisNum?: number;
}

const AXIS_COLORS: Record<string, string> = { V: '#8884d8', H: '#82ca9d', A: '#FF0000' };

// Generate historical data for a point metric with V, H, A
const generatePointHistoricalData = (pointData: PointData, timeRange: number) => {
  let timeStamps: string[];
  switch (timeRange) {
    case 0: timeStamps = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00']; break;
    case 1: timeStamps = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; break;
    case 2: timeStamps = ['Week 1', 'Week 2', 'Week 3', 'Week 4']; break;
    case 3: timeStamps = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']; break;
    default: timeStamps = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'];
  }
  return timeStamps.map((time, index) => {
    const multiplier = 0.8 + (Math.sin(index * 0.5) * 0.2) + (index % 2 === 0 ? 0.1 : 0);
    return {
      time,
      V: pointData.V * (multiplier + 0.2),
      H: pointData.H * (multiplier + 0.15),
      A: pointData.A * (multiplier + 0.25),
    };
  });
};


function applyFloor(data: any[], floor: number): any[] {
  if (floor <= 0) return data;
  return data.map(entry => {
    const out: any = { time: entry.time };
    for (const [k, v] of Object.entries(entry)) {
      if (k === 'time') continue;
      out[k] = typeof v === 'number' && v < floor ? null : v;
    }
    return out;
  });
}

function FloorSlider({ floor, setFloor, yMax }: { floor: number; setFloor: (v: number) => void; yMax: number }) {
  const step = yMax > 0 ? yMax / 200 : 0.001;
  return (
    <div className="flex flex-col items-center justify-between shrink-0" style={{ height: 300, width: 28, paddingTop: 6, paddingBottom: 4 }}>
      <span className="font-mono tabular-nums text-center" style={{ fontSize: 9, color: '#64748b', minHeight: 12, lineHeight: '12px', writingMode: 'horizontal-tb' }}>
        {floor > 0 ? floor.toFixed(3) : ''}
      </span>
      <input
        type="range" min={0} max={yMax || 1} step={step}
        value={Math.min(floor, yMax || 1)}
        onChange={e => setFloor(Number(e.target.value))}
        style={{ writingMode: 'vertical-lr', direction: 'rtl', height: 240, width: 18, cursor: 'pointer', accentColor: '#64748b', flexShrink: 0 }}
        title="Drag to filter: hides data below this value"
      />
      <span style={{ fontSize: 9, color: '#9ca3af' }}>0</span>
    </div>
  );
}

function calcYMax(data: any[], threshMod?: number | null): number {
  const vals = data.flatMap(d =>
    Object.entries(d)
      .filter(([k]) => k !== 'time')
      .map(([, v]) => v as number)
      .filter(v => v != null && !isNaN(v))
  );
  const dataMax = vals.length > 0 ? Math.max(...vals) : 0;
  const top = Math.max(dataMax, threshMod ?? 0);
  return top > 0 ? parseFloat((top * 1.2).toFixed(4)) : 1;
}

// Build Map<bucket, {file_id, measured_at}> for waveform dots from raw trend rows.
function buildWfMarkers(rows: any[], pointName: string): Map<string, { file_id: number; measured_at: string }> {
  const result = new Map<string, { file_id: number; measured_at: string }>();
  for (const r of rows) {
    if (!r.file_id || r.reason !== 2 || r.point_name !== pointName) continue;
    const bucket = (r.measured_at || '').substring(0, 10).replace(/\//g, '-');
    if (!result.has(bucket)) result.set(bucket, { file_id: r.file_id, measured_at: r.measured_at });
  }
  return result;
}

// Find nearest time bucket that has a waveform, searching outward from activeIdx.
function findNearestWfPt(
  data: any[],
  idx: number,
  markersMap: Map<string, { file_id: number; measured_at: string }> | undefined,
  maxSearch = 10
): { time: string; measured_at: string } | null {
  if (!markersMap) return null;
  for (let d = 0; d <= maxSearch; d++) {
    for (const off of d === 0 ? [0] : [d, -d]) {
      const i = idx + off;
      if (i < 0 || i >= data.length) continue;
      const time = data[i]?.time;
      if (time && markersMap.has(time)) return { time, measured_at: markersMap.get(time)!.measured_at };
    }
  }
  return null;
}

// Format 'YYYY/MM/DD HH:MM:SS' → "Apr 21 9:26 PM — click to load"
function fmtWfLabel(measured_at: string): string {
  const [dp, tp = '00:00:00'] = measured_at.split(' ');
  const [, mo, dy] = dp.split('/');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  let h = parseInt(tp.split(':')[0]);
  const mn = tp.split(':')[1];
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${months[parseInt(mo)-1]} ${parseInt(dy)} ${h}:${mn} ${ap} — click to load`;
}

// Chart Container with Copy functionality
function ChartContainer({ children, title, onExpand }: { children: React.ReactNode; title: string; onExpand?: () => void }) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyChart = async () => {
    if (!chartRef.current) return;
    try {
      const svgElement = chartRef.current.querySelector('svg');
      if (!svgElement) return;
      const rect = svgElement.getBoundingClientRect();
      const canvas = document.createElement('canvas');
      canvas.width = rect.width * 2;
      canvas.height = rect.height * 2;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(2, 2);
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, rect.width, rect.height);
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = async () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        URL.revokeObjectURL(url);
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          try {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch {}
        });
      };
      img.src = url;
    } catch {}
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-gray-900">{title}</h3>
        <div className="flex items-center gap-2">
          {onExpand && (
            <button onClick={onExpand} className="p-2 hover:bg-gray-100 rounded-lg transition-all group" title="Expand chart">
              <Maximize2 className="w-4 h-4 text-gray-600 group-hover:text-gray-900" />
            </button>
          )}
          <button onClick={handleCopyChart} className="p-2 hover:bg-gray-100 rounded-lg transition-all group" title="Copy chart to clipboard">
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-600 group-hover:text-gray-900" />}
          </button>
        </div>
      </div>
      <div ref={chartRef}>{children}</div>
    </div>
  );
}

const PV_PLUS_FAULT_COLOURS: Record<string, string> = { highly_periodic: '#dc2626', bearing_fault: '#dc2626', cavitation_noise: '#6b7280', lubrication: '#d97706', early_fault: '#0891b2', healthy: '#16a34a' };
const PV_PLUS_FAULT_LABELS: Record<string, string>  = { highly_periodic: 'Highly Periodic', bearing_fault: 'Highly Periodic', cavitation_noise: 'Cavitation/noise', lubrication: 'Lubrication', early_fault: 'Early fault', healthy: 'Healthy' };

export function PointDetail({ point, onBack, onThresholdsUpdate, initialAxisNum }: PointDetailProps) {
  // Shared time range for all charts (days)
  const [trendDays, setTrendDays] = useState(90);
  // Active tab: 2=Velocity, 0=Acceleration, 6=Envelope, -1=PeakVue
  const [primaryUnit, setPrimaryUnit] = useState<number>(2);
  // PeakVue per-axis data
  const [pvAxisData, setPvAxisData] = useState<Record<string, { time: string; value: number; ft?: string; pp?: number; rel?: string; at?: string }[]>>({});
  const [pvAxisThresholds, setPvAxisThresholds] = useState<Record<string, { moderate: number; severe: number; is_fallback: boolean } | null>>({});
  const [pvLoading, setPvLoading] = useState(false);
  const [pvHiddenAxes, setPvHiddenAxes] = useState<Set<string>>(new Set());
  const togglePvAxis = (lbl: string) => setPvHiddenAxes(prev => { const s = new Set(prev); if (s.has(lbl)) s.delete(lbl); else s.add(lbl); return s; });
  const pvHoverRef = useRef<{ t: string; axis: string } | null>(null);

  const [velocityThresholds, setVelocityThresholds] = useState(point.velocityThresholds ?? { yellow: 0, red: 0 });
  const [accelerationThresholds, setAccelerationThresholds] = useState(point.accelerationThresholds ?? { yellow: 0, red: 0 });
  const [envelopeThresholds, setEnvelopeThresholds] = useState(point.envelopeThresholds ?? { yellow: 0, red: 0 });
  const [temperatureThresholds, setTemperatureThresholds] = useState(point.temperatureThresholds ?? { yellow: 0, red: 0 });
  // Per-axis thresholds for dynamic chart reference lines (read-only, driven by prop)
  const velThrByAxis = point.velocityThresholdsByAxis ?? {};
  const accelThrByAxis = point.accelerationThresholdsByAxis ?? {};
  const envThrByAxis = point.envelopeThresholdsByAxis ?? {};

  const [velocityDirty, setVelocityDirty] = useState(false);
  const [accelerationDirty, setAccelerationDirty] = useState(false);
  const [envelopeDirty, setEnvelopeDirty] = useState(false);
  const [savingMetric, setSavingMetric] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [velFloor,   setVelFloor]   = useState(0);
  const [accelFloor, setAccelFloor] = useState(0);
  const [envFloor,   setEnvFloor]   = useState(0);
  const [tempFloor,  setTempFloor]  = useState(0);

  type AxisRow = { time: string; V: number | null; H: number | null; A: number | null };
  const [trendData, setTrendData] = useState<Record<number, AxisRow[]>>({ 0: [], 2: [], 6: [] });
  const [sensorTempData, setSensorTempData] = useState<{ time: string; temp: number }[]>([]);

  // Waveform state
  const [wfMarkersMap, setWfMarkersMap] = useState<Record<number, Map<string, { file_id: number; measured_at: string }>>>({});
  const [waveformJumpTo, setWaveformJumpTo] = useState<WaveformJumpTo | null>(null);
  const [wfHover, setWfHover] = useState<{ unitCode: number; time: string; measured_at: string } | null>(null);
  const wfHoverRef = useRef<{ unitCode: number; time: string; measured_at: string } | null>(null);
  wfHoverRef.current = wfHover;

  const MM_TO_IN = 1 / 25.4;

  const buildAxisRows = (rows: any[], unitCode: number): AxisRow[] => {
    const scale = unitCode === 2 ? MM_TO_IN : 1;
    const buckets = new Map<string, { V: number[]; H: number[]; A: number[] }>();
    for (const r of rows.filter(r => r.point_name === point.name)) {
      if (r.value == null) continue;
      const day = (r.measured_at || '').substring(0, 10).replace(/\//g, '-');
      if (!buckets.has(day)) buckets.set(day, { V: [], H: [], A: [] });
      const b = buckets.get(day)!;
      const v = r.value * scale;
      if      (r.axis_num === 2) b.V.push(v);
      else if (r.axis_num === 1) b.H.push(v);
      else if (r.axis_num === 3) b.A.push(v);
    }
    const decimals = unitCode === 2 ? 4 : 3;
    const peak = (arr: number[]): number | null =>
      arr.length ? parseFloat(Math.max(...arr).toFixed(decimals)) : null;
    return Array.from(buckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, b]) => ({ time: day, V: peak(b.V), H: peak(b.H), A: peak(b.A) }));
  };

  useEffect(() => {
    if (!point.machineId) return;
    apiFetch<any[]>(`/machines/${point.machineId}/trend?unit_code=2&days=${trendDays}`)
      .then(rows => {
        setTrendData(prev => ({ ...prev, 2: buildAxisRows(rows, 2) }));
        setWfMarkersMap(prev => ({ ...prev, 2: buildWfMarkers(rows, point.name) }));
      }).catch(() => {});
  }, [point.machineId, point.name, trendDays]);

  useEffect(() => {
    if (!point.machineId) return;
    apiFetch<any[]>(`/machines/${point.machineId}/trend?unit_code=0&days=${trendDays}`)
      .then(rows => {
        setTrendData(prev => ({ ...prev, 0: buildAxisRows(rows, 0) }));
        setWfMarkersMap(prev => ({ ...prev, 0: buildWfMarkers(rows, point.name) }));
      }).catch(() => {});
  }, [point.machineId, point.name, trendDays]);

  useEffect(() => {
    if (!point.machineId) return;
    apiFetch<any[]>(`/machines/${point.machineId}/trend?unit_code=6&days=${trendDays}`)
      .then(rows => {
        setTrendData(prev => ({ ...prev, 6: buildAxisRows(rows, 6) }));
        setWfMarkersMap(prev => ({ ...prev, 6: buildWfMarkers(rows, point.name) }));
      }).catch(() => {});
  }, [point.machineId, point.name, trendDays]);

  useEffect(() => {
    if (!point.machineId) return;
    apiFetch<any[]>(`/machines/${point.machineId}/sensor-health?days=${trendDays}`)
      .then(rows => {
        const mine = (rows ?? []).filter(r => r.point_name === point.name && r.sensor_temperature != null);
        const buckets = new Map<string, number[]>();
        for (const r of mine) {
          const day = (r.polled_at || '').substring(0, 10);
          if (!buckets.has(day)) buckets.set(day, []);
          buckets.get(day)!.push(r.sensor_temperature);
        }
        const result = Array.from(buckets.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([day, vals]) => ({
            time: day,
            temp: parseFloat((vals.reduce((s, v) => s + v, 0) / vals.length * 9 / 5 + 32).toFixed(1)),
          }));
        setSensorTempData(result);
      })
      .catch(() => setSensorTempData([]));
  }, [point.machineId, point.name, trendDays]);

  // PeakVue per-axis fetch
  useEffect(() => {
    const axisIdsByLabel = point.axisIdsByLabel;
    if (!point.machineId || !axisIdsByLabel || Object.keys(axisIdsByLabel).length === 0) return;
    setPvHiddenAxes(new Set());
    setPvLoading(true);
    const fetchAxis = async (label: string, axisId: number) => {
      const data = await apiFetch<any>(`/machines/${point.machineId}/peakvue-trend?days=${trendDays}&axis_id=${axisId}`);
      // Axis-level response: { level: 'axis', readings: [...], alarm_thresholds: {...} }
      const readings: any[] = data?.level === 'axis'
        ? (data?.readings ?? [])
        : (data?.points ?? []).flatMap((pt: any) => pt.readings ?? []);
      const thr = data?.alarm_thresholds
        ? { moderate: data.alarm_thresholds.threshold_moderate, severe: data.alarm_thresholds.threshold_severe, is_fallback: !!data.alarm_thresholds.is_emerson_fallback }
        : null;
      const weekly = trendDays > 30;
      const bucketMap = new Map<string, any>();
      for (const r of readings) {
        const dt: string = r.measured_at || '';
        let bucket: string;
        if (weekly) {
          const d = new Date(dt.substring(0, 10).replace(/\//g, '-'));
          const day = d.getDay();
          d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
          bucket = d.toISOString().substring(0, 10);
        } else { bucket = dt.substring(0, 10).replace(/\//g, '-'); }
        const prev = bucketMap.get(bucket);
        if (!prev || r.max_peak_g > prev.value) {
          bucketMap.set(bucket, {
            time: bucket,
            value: r.max_peak_g,
            at: r.measured_at,
            ...(r.max_peak_g >= 5 && r.fault_type ? { ft: r.fault_type, pp: r.periodic_pct, rel: r.plus_reliability } : {}),
          });
        }
      }
      return { rows: Array.from(bucketMap.values()).sort((a, b) => a.time.localeCompare(b.time)), thr };
    };
    Promise.all(
      Object.entries(axisIdsByLabel).map(([label, id]) => fetchAxis(label, id).then(({ rows, thr }) => [label, rows, thr] as const))
    ).then(results => {
      const outData: Record<string, any[]> = {};
      const outThr: Record<string, { moderate: number; severe: number; is_fallback: boolean } | null> = {};
      for (const [label, rows, thr] of results) { outData[label] = rows; outThr[label] = thr; }
      setPvAxisData(outData);
      setPvAxisThresholds(outThr);
    }).catch(() => {}).finally(() => setPvLoading(false));
  }, [point.machineId, point.axisIdsByLabel, trendDays]);

  // Memoized raw (real data or fallback) and filtered arrays
  const rawVel   = useMemo(() => trendData[2].length ? trendData[2] : generatePointHistoricalData(point.velocity,     trendDays > 7 ? 2 : 1), [trendData[2],  trendDays]);
  const rawAccel = useMemo(() => trendData[0].length ? trendData[0] : generatePointHistoricalData(point.acceleration, trendDays > 7 ? 2 : 1), [trendData[0],  trendDays]);
  const rawEnv   = useMemo(() => trendData[6].length ? trendData[6] : generatePointHistoricalData(point.envelope,     trendDays > 7 ? 2 : 1), [trendData[6],  trendDays]);
  const filteredVel   = useMemo(() => applyFloor(rawVel,         velFloor),   [rawVel,         velFloor]);
  const filteredAccel = useMemo(() => applyFloor(rawAccel,     accelFloor),   [rawAccel,     accelFloor]);
  const filteredEnv   = useMemo(() => applyFloor(rawEnv,         envFloor),   [rawEnv,         envFloor]);
  const filteredTemp  = useMemo(() => applyFloor(sensorTempData, tempFloor),  [sensorTempData, tempFloor]);

  const UNIT_CODE: Record<string, number> = { velocity: 2, acceleration: 0, envelope: 6, temperature: 20 };

  const handleThresholdChange = (metric: string, level: 'yellow' | 'red', value: number) => {
    // When moderate (yellow) changes, auto-set severe (red) = moderate × 1.5
    const applyLevel = (base: Thresholds): Thresholds => {
      if (level === 'yellow') return { yellow: value, red: parseFloat((value * 1.5).toFixed(6)) };
      return { ...base, red: value };
    };
    if (metric === 'velocity') {
      const updated = applyLevel(velocityThresholds);
      setVelocityThresholds(updated);
      setVelocityDirty(true);
      if (onThresholdsUpdate) onThresholdsUpdate(point.id, 'velocity', updated);
    } else if (metric === 'acceleration') {
      const updated = applyLevel(accelerationThresholds);
      setAccelerationThresholds(updated);
      setAccelerationDirty(true);
      if (onThresholdsUpdate) onThresholdsUpdate(point.id, 'acceleration', updated);
    } else if (metric === 'envelope') {
      const updated = applyLevel(envelopeThresholds);
      setEnvelopeThresholds(updated);
      setEnvelopeDirty(true);
      if (onThresholdsUpdate) onThresholdsUpdate(point.id, 'envelope', updated);
    } else if (metric === 'temperature') {
      const updated = applyLevel(temperatureThresholds);
      setTemperatureThresholds(updated);
      if (onThresholdsUpdate) onThresholdsUpdate(point.id, 'temperature', updated);
    }
  };

  const handleSave = async (metric: string) => {
    if (!point.axisIds?.length) return;
    const unitCode = UNIT_CODE[metric];
    if (unitCode == null) return;
    const threshMap: Record<string, Thresholds> = {
      velocity: velocityThresholds,
      acceleration: accelerationThresholds,
      envelope: envelopeThresholds,
    };
    const t = threshMap[metric];
    if (!t) return;

    setSavingMetric(metric);
    setSaveError(null);
    try {
      await Promise.all(
        point.axisIds.map(axisId =>
          apiFetch(`/alarm-manager/${axisId}/${unitCode}`, {
            method: 'PATCH',
            body: JSON.stringify({ moderate_override: t.yellow, severe_override: t.red }),
          })
        )
      );
      if (metric === 'velocity') setVelocityDirty(false);
      else if (metric === 'acceleration') setAccelerationDirty(false);
      else if (metric === 'envelope') setEnvelopeDirty(false);
    } catch (err: any) {
      setSaveError(err?.message ?? 'Save failed — check you have admin access');
    } finally {
      setSavingMetric(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'low': return 'bg-green-500';
      case 'medium': return 'bg-yellow-500';
      case 'high': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Render filled dot (axis color) when waveform available, small semi-transparent dot otherwise
  const makeDot = (unitCode: number, axisColor: string) => (p: any) => {
    const { cx, cy, payload, index } = p;
    if (!cx || !cy) return <circle key={index} r={0} />;
    const wf = wfMarkersMap[unitCode]?.get(payload.time);
    if (wf) return <circle key={index} cx={cx} cy={cy} r={4} fill={axisColor} stroke="white" strokeWidth={1.5} />;
    return <circle key={index} cx={cx} cy={cy} r={2} fill={axisColor} fillOpacity={0.5} />;
  };

  // V/H/A series toggle state — pre-select a single axis when coming from sidebar axis click
  const [hiddenAxes, setHiddenAxes] = useState<Set<string>>(() => {
    if (!initialAxisNum) return new Set<string>();
    const shown = initialAxisNum === 1 ? 'H' : initialAxisNum === 2 ? 'V' : 'A';
    return new Set(['H', 'V', 'A'].filter(a => a !== shown));
  });
  const toggleAxis = (axis: string) => setHiddenAxes(prev => {
    const next = new Set(prev);
    if (next.has(axis)) next.delete(axis); else next.add(axis);
    return next;
  });

  // Sync threshold sliders to the visible axis when toggling (only when slider hasn't been manually edited)
  useEffect(() => {
    if (velocityDirty || Object.keys(velThrByAxis).length === 0) return;
    const visible = (['V', 'H', 'A'] as const).filter(a => !hiddenAxes.has(a));
    if (visible.length === 1 && velThrByAxis[visible[0]]) {
      setVelocityThresholds(velThrByAxis[visible[0]]);
    } else {
      const ys = visible.map(a => velThrByAxis[a]?.yellow ?? 0).filter(v => v > 0);
      const rs = visible.map(a => velThrByAxis[a]?.red   ?? 0).filter(v => v > 0);
      if (ys.length) setVelocityThresholds({ yellow: Math.max(...ys), red: Math.max(...rs) });
    }
  }, [hiddenAxes]);

  useEffect(() => {
    if (accelerationDirty || Object.keys(accelThrByAxis).length === 0) return;
    const visible = (['V', 'H', 'A'] as const).filter(a => !hiddenAxes.has(a));
    if (visible.length === 1 && accelThrByAxis[visible[0]]) {
      setAccelerationThresholds(accelThrByAxis[visible[0]]);
    } else {
      const ys = visible.map(a => accelThrByAxis[a]?.yellow ?? 0).filter(v => v > 0);
      const rs = visible.map(a => accelThrByAxis[a]?.red   ?? 0).filter(v => v > 0);
      if (ys.length) setAccelerationThresholds({ yellow: Math.max(...ys), red: Math.max(...rs) });
    }
  }, [hiddenAxes]);

  useEffect(() => {
    if (envelopeDirty || Object.keys(envThrByAxis).length === 0) return;
    const visible = (['V', 'H', 'A'] as const).filter(a => !hiddenAxes.has(a));
    if (visible.length === 1 && envThrByAxis[visible[0]]) {
      setEnvelopeThresholds(envThrByAxis[visible[0]]);
    } else {
      const ys = visible.map(a => envThrByAxis[a]?.yellow ?? 0).filter(v => v > 0);
      const rs = visible.map(a => envThrByAxis[a]?.red   ?? 0).filter(v => v > 0);
      if (ys.length) setEnvelopeThresholds({ yellow: Math.max(...ys), red: Math.max(...rs) });
    }
  }, [hiddenAxes]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group">
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <div className="flex items-center gap-3">
          <h2 className="text-gray-900 text-xl font-semibold">{point.name} — Detailed Analysis</h2>
          <div className={`w-3 h-3 rounded-full ${getStatusColor(point.status)}`}></div>
        </div>
      </div>

      <div className="space-y-6">
          {/* Current Values */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
              <div className="text-gray-600 mb-2">Velocity</div>
              <div className="text-gray-900">V: {point.velocity.V.toFixed(2)} in/s</div>
              <div className="text-gray-900">H: {point.velocity.H.toFixed(2)} in/s</div>
              <div className="text-gray-900">A: {point.velocity.A.toFixed(2)} in/s</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
              <div className="text-gray-600 mb-2">Acceleration</div>
              <div className="text-gray-900">V: {point.acceleration.V.toFixed(2)} G&apos;s</div>
              <div className="text-gray-900">H: {point.acceleration.H.toFixed(2)} G&apos;s</div>
              <div className="text-gray-900">A: {point.acceleration.A.toFixed(2)} G&apos;s</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
              <div className="text-gray-600 mb-2">Envelope</div>
              <div className="text-gray-900">V: {point.envelope.V.toFixed(2)} G&apos;s</div>
              <div className="text-gray-900">H: {point.envelope.H.toFixed(2)} G&apos;s</div>
              <div className="text-gray-900">A: {point.envelope.A.toFixed(2)} G&apos;s</div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
              <div className="text-gray-600 mb-2">Temperature</div>
              <div className="text-gray-900">V: {point.temperature.V.toFixed(0)}&deg;F</div>
              <div className="text-gray-900">H: {point.temperature.H.toFixed(0)}&deg;F</div>
              <div className="text-gray-900">A: {point.temperature.A.toFixed(0)}&deg;F</div>
            </div>
          </div>

          {/* Primary trend card — mirrors machine page: tabs + shared time range */}
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-3 border border-gray-200 shadow-sm">
            {/* Controls row */}
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                {([
                  { unit: 2,  label: 'Velocity' },
                  { unit: 0,  label: 'Acceleration' },
                  { unit: 6,  label: 'Envelope' },
                  ...(point.axisIdsByLabel && Object.keys(point.axisIdsByLabel).length > 0 ? [{ unit: -1 as number, label: 'Impact View' }] : []),
                ] as { unit: number; label: string }[]).map(({ unit, label }) => (
                  <button key={unit} onClick={() => setPrimaryUnit(unit)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${primaryUnit === unit ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  >{label}</button>
                ))}
              </div>
              <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
                {([{d:7,l:'7D'},{d:30,l:'30D'},{d:90,l:'90D'},{d:180,l:'180D'},{d:365,l:'1Y'}] as {d:number;l:string}[]).map(({d,l},i) => (
                  <button key={d} onClick={() => setTrendDays(d)}
                    className={`px-2.5 py-1.5 ${i > 0 ? 'border-l border-gray-200' : ''} ${trendDays === d ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >{l}</button>
                ))}
              </div>
            </div>

            {/* Chart body */}
            {primaryUnit === -1 ? (
              pvLoading ? (
                <div className="bg-gray-50 rounded animate-pulse" style={{ height: 300 }} />
              ) : Object.keys(pvAxisData).length === 0 ? (
                <div className="flex items-center justify-center text-gray-400 text-sm" style={{ height: 300 }}>No Impact View data available for this point</div>
              ) : (() => {
                const allTimes = Array.from(new Set(Object.values(pvAxisData).flatMap(rows => rows.map(r => r.time)))).sort();
                const cd = allTimes.map(t => {
                  const row: any = { time: t };
                  for (const [lbl, rows] of Object.entries(pvAxisData)) {
                    const r = rows.find(x => x.time === t);
                    if (!r) continue;
                    row[lbl] = r.value;
                    row[`${lbl}__at`] = r.at;
                    if (r.ft) { row[`${lbl}__ft`] = r.ft; row[`${lbl}__pp`] = r.pp; row[`${lbl}__rel`] = r.rel; }
                  }
                  return row;
                });
                const axisLabels = Object.keys(pvAxisData);
                const visibleAxisLabels = axisLabels.filter(l => !pvHiddenAxes.has(l));
                return (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={cd} margin={{ top: 5, right: 24, left: 0, bottom: 5 }} style={{ cursor: 'pointer' }}
                        onMouseMove={(s: any) => {
                          const ap = s?.activePayload;
                          if (s?.isTooltipActive && ap?.length > 0) {
                            const ak = ap.find((p: any) => !String(p.name).includes('__'))?.name;
                            const at = ak ? ap[0]?.payload?.[`${ak}__at`] : null;
                            if (at && ak) pvHoverRef.current = { t: at, axis: ak };
                          } else { pvHoverRef.current = null; }
                        }}
                        onMouseLeave={() => { pvHoverRef.current = null; }}
                        onClick={() => { const h = pvHoverRef.current; if (h) setWaveformJumpTo({ measured_at: h.t, point_name: point.name, signal: 'peakvue' }); }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="time" stroke="#9CA3AF" tick={{ fontSize: 10 }} minTickGap={40} />
                        <YAxis stroke="#9CA3AF" tick={{ fontSize: 10 }} label={{ value: 'G', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#6b7280' } }} />
                        <Tooltip content={({ active, payload, label }: any) => {
                          if (!active || !payload?.length) return null;
                          const seen = new Set<string>();
                          const deduped = payload.filter((p: any) => { const k = String(p.dataKey); if (seen.has(k)) return false; seen.add(k); return true; });
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg p-2 text-xs shadow">
                              <div className="font-medium mb-1 text-gray-700">{label}</div>
                              {deduped.map((p: any, i: number) => {
                                const name = String(p.dataKey ?? '');
                                if (name.includes('__')) return null;
                                const ft = p.payload?.[`${name}__ft`]; const pp = p.payload?.[`${name}__pp`]; const rel = p.payload?.[`${name}__rel`];
                                const val = typeof p.value === 'number' ? p.value.toFixed(3) + ' G' : p.value;
                                const detail = ft ? `  |  ${PV_PLUS_FAULT_LABELS[ft] ?? ft}  (${pp != null ? Number(pp).toFixed(0) : '—'}% periodic)${rel === 'preliminary' ? '  ⚠' : ''}` : '';
                                return <div key={i} style={{ color: p.color }}>{name} : {val}{detail}</div>;
                              })}
                            </div>
                          );
                        }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {/* Per-axis moderate / severe alarm thresholds */}
                        {axisLabels.map(lbl => {
                          const t = pvAxisThresholds[lbl];
                          if (!t) return null;
                          const c = AXIS_COLORS[lbl] ?? '#6b7280';
                          const decimals = 2;
                          return (
                            <Fragment key={`thr-${lbl}`}>
                              {t.moderate > 0 && <ReferenceLine y={t.moderate} stroke={c} strokeWidth={1.5} strokeDasharray="6 3"
                                label={{ value: `${lbl} Mod: ${t.moderate.toFixed(decimals)}G`, position: 'insideTopRight', fill: c, fontSize: 9 }} />}
                              {t.severe   > 0 && <ReferenceLine y={t.severe}   stroke={c} strokeWidth={1}   strokeDasharray="3 2"
                                label={{ value: `${lbl} Sev: ${t.severe.toFixed(decimals)}G`,   position: 'insideBottomRight', fill: c, fontSize: 9 }} />}
                            </Fragment>
                          );
                        })}
                        {axisLabels.map((lbl) => !visibleAxisLabels.includes(lbl) ? null : (
                          <Fragment key={lbl}>
                            <Line type="monotone" dataKey={lbl} stroke={AXIS_COLORS[lbl] ?? '#2563eb'} strokeWidth={1.5} connectNulls strokeDasharray="4 4" strokeOpacity={0.4} legendType="none" isAnimationActive={false} />
                            <Line type="monotone" dataKey={lbl} stroke={AXIS_COLORS[lbl] ?? '#2563eb'} strokeWidth={2} connectNulls name={lbl} isAnimationActive={false}
                              dot={(props: any) => {
                                const { cx, cy, payload } = props;
                                if (cx == null || cy == null) return <></>;
                                const ft = payload[`${lbl}__ft`]; const rel = payload[`${lbl}__rel`]; const pp = payload[`${lbl}__pp`];
                                const ppNum = pp != null ? Number(pp) : 0;
                                const isHP = ft === 'highly_periodic' || ft === 'bearing_fault';
                                if (isHP && ppNum >= 75) return <circle key={`pv-${lbl}-${payload.time}`} cx={cx} cy={cy} r={rel === 'high' ? 6 : 4} fill={PV_PLUS_FAULT_COLOURS['highly_periodic']} stroke="white" strokeWidth={2} style={{ cursor: 'pointer' }} />;
                                if (isHP && ppNum >= 50) return <circle key={`pv-${lbl}-${payload.time}`} cx={cx} cy={cy} r={rel === 'high' ? 6 : 4} fill="#eab308" stroke="white" strokeWidth={2} style={{ cursor: 'pointer' }} />;
                                if (ft && !isHP) return <circle key={`pv-${lbl}-${payload.time}`} cx={cx} cy={cy} r={rel === 'high' ? 6 : 4} fill={PV_PLUS_FAULT_COLOURS[ft] ?? '#6b7280'} stroke="white" strokeWidth={2} style={{ cursor: 'pointer' }} />;
                                return <circle key={`pv-${lbl}-${payload.time}`} cx={cx} cy={cy} r={2} fill={AXIS_COLORS[lbl] ?? '#2563eb'} fillOpacity={0.5} />;
                              }}
                            />
                          </Fragment>
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap justify-center gap-1.5 pt-1 pb-0.5">
                      {axisLabels.map(lbl => {
                        const color = AXIS_COLORS[lbl] ?? '#2563eb';
                        const isHidden = pvHiddenAxes.has(lbl);
                        return (
                          <button key={lbl} onClick={() => togglePvAxis(lbl)}
                            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-all select-none"
                            style={{ borderColor: color, background: isHidden ? '#f8fafc' : 'white', opacity: isHidden ? 0.45 : 1 }}
                          >
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: isHidden ? 'transparent' : color, border: `1.5px solid ${color}` }} />
                            <span style={{ color: isHidden ? '#9ca3af' : color }}>{lbl}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                      <span className="text-gray-400">Plus dots (≥5G):</span>
                      {[['hp','#dc2626','Highly Periodic (≥75%)'],['hp2','#eab308','Moderately Periodic (50–74%)'],['he','#16a34a','Non-periodic']].map(([k,col,lbl]) => (
                        <span key={k} className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: col }} />{lbl}</span>
                      ))}
                    </div>
                  </>
                );
              })()
            ) : (() => {
              // Standard chart (Velocity / Acceleration / Envelope)
              const uc = primaryUnit;
              const filtered    = uc === 2 ? filteredVel   : uc === 0 ? filteredAccel : filteredEnv;
              const thrByAxis   = uc === 2 ? velThrByAxis  : uc === 0 ? accelThrByAxis : envThrByAxis;
              const thresholds  = uc === 2 ? velocityThresholds : uc === 0 ? accelerationThresholds : envelopeThresholds;
              const metricKey   = uc === 2 ? 'velocity'    : uc === 0 ? 'acceleration' : 'envelope';
              const dirty       = uc === 2 ? velocityDirty : uc === 0 ? accelerationDirty : envelopeDirty;
              const unitLabel   = uc === 2 ? 'in/s'        : uc === 0 ? 'G' : 'GE';
              const decimals    = uc === 2 ? 3             : 4;
              const step        = uc === 2 ? 0.001         : 0.0001;
              const floor       = uc === 2 ? velFloor      : uc === 0 ? accelFloor : envFloor;
              const setFloor    = uc === 2 ? setVelFloor   : uc === 0 ? setAccelFloor : setEnvFloor;
              const wfMarkers   = wfMarkersMap[uc];
              const hasByAxis   = Object.keys(thrByAxis).length > 0;
              const visibleAxes = (['V', 'H', 'A'] as const).filter(a => !hiddenAxes.has(a));
              const visAxes     = visibleAxes;
              const axisLabel   = visAxes.length === 1 ? `${visAxes[0]}-Axis ` : '';
              const maxYellow   = hasByAxis ? Math.max(0, ...visibleAxes.map(a => thrByAxis[a]?.yellow ?? 0)) : thresholds.yellow;
              const yMax        = calcYMax(filtered, maxYellow);
              return (
                <>
                  <div className="flex gap-2 items-start">
                    <FloorSlider floor={floor} setFloor={setFloor} yMax={yMax} />
                    <div className="flex-1 min-w-0" style={{ cursor: wfHover?.unitCode === uc ? 'pointer' : 'default' }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={filtered} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          onMouseMove={(s: any) => {
                            if (s.isTooltipActive && s.activeTooltipIndex !== undefined) {
                              const nw = findNearestWfPt(filtered, s.activeTooltipIndex, wfMarkers);
                              setWfHover(nw ? { unitCode: uc, ...nw } : null);
                            } else { setWfHover(null); }
                          }}
                          onMouseLeave={() => setWfHover(null)}
                          onClick={() => { const h = wfHoverRef.current; if (h?.unitCode === uc) setWaveformJumpTo({ measured_at: h.measured_at, point_name: point.name }); }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, yMax]} tickFormatter={v => v.toFixed(decimals)} />
                          <Tooltip formatter={(v: number) => [typeof v === 'number' ? v.toFixed(decimals) : v, '']} />
                          {hasByAxis
                            ? visibleAxes.map(a => {
                                const t = thrByAxis[a]; if (!t) return null;
                                const c = AXIS_COLORS[a]; const single = visibleAxes.length === 1;
                                return (
                                  <Fragment key={a}>
                                    {t.yellow > 0 && <ReferenceLine y={t.yellow} stroke={c} strokeWidth={2} strokeDasharray="6 3" label={{ value: single ? `Mod: ${t.yellow.toFixed(decimals)}` : `${a} ${t.yellow.toFixed(decimals)}`, position: 'insideTopRight', fill: c, fontSize: 10 }} />}
                                    {t.red    > 0 && <ReferenceLine y={t.red}    stroke={c} strokeWidth={1.5} strokeDasharray="3 2" label={{ value: single ? `Sev: ${t.red.toFixed(decimals)}`    : `${a} ${t.red.toFixed(decimals)}`,    position: 'insideBottomRight', fill: c, fontSize: 10 }} />}
                                  </Fragment>
                                );
                              })
                            : <>
                                {thresholds.yellow > 0 && <ReferenceLine y={thresholds.yellow} stroke="#F59E0B" strokeWidth={2} strokeDasharray="6 3" label={{ value: `Mod: ${thresholds.yellow.toFixed(decimals)}`, position: 'insideTopRight', fill: '#f59e0b', fontSize: 10 }} />}
                                {thresholds.red    > 0 && <ReferenceLine y={thresholds.red}    stroke="#EF4444" strokeWidth={2} strokeDasharray="6 3" label={{ value: `Sev: ${thresholds.red.toFixed(decimals)}`,    position: 'insideTopRight', fill: '#ef4444', fontSize: 10 }} />}
                              </>
                          }
                          {floor > 0 && <ReferenceLine y={floor} stroke="#64748b" strokeDasharray="4 4" strokeWidth={1} label={{ value: `Floor: ${floor.toFixed(3)}`, position: 'insideTopLeft', fill: '#64748b', fontSize: 10 }} />}
                          {wfHover?.unitCode === uc && <ReferenceLine x={wfHover.time} stroke="#06b6d4" strokeWidth={2} label={{ value: fmtWfLabel(wfHover.measured_at), position: 'insideBottomLeft', fill: '#06b6d4', fontSize: 9, fontWeight: 600 }} />}
                          <Line type="monotone" dataKey="V" stroke={AXIS_COLORS.V} isAnimationActive={false} connectNulls dot={makeDot(uc, AXIS_COLORS.V)} hide={hiddenAxes.has('V')} />
                          <Line type="monotone" dataKey="H" stroke={AXIS_COLORS.H} isAnimationActive={false} connectNulls dot={makeDot(uc, AXIS_COLORS.H)} hide={hiddenAxes.has('H')} />
                          <Line type="monotone" dataKey="A" stroke={AXIS_COLORS.A} isAnimationActive={false} connectNulls dot={makeDot(uc, AXIS_COLORS.A)} hide={hiddenAxes.has('A')} />
                        </LineChart>
                      </ResponsiveContainer>
                      <div className="flex justify-center gap-2 pt-1">
                        {(['V', 'H', 'A'] as const).map(axis => (
                          <button key={axis} onClick={() => toggleAxis(axis)}
                            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-all select-none"
                            style={{ borderColor: AXIS_COLORS[axis], background: 'white', opacity: hiddenAxes.has(axis) ? 0.4 : 1 }}>
                            <span className="w-2 h-2 rounded-full" style={{ background: AXIS_COLORS[axis] }} />{axis}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Alarm threshold controls */}
                  <div className="mt-4 space-y-3 border-t border-gray-100 pt-3">
                    {Object.keys(thrByAxis).length > 0 && (
                      <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
                        <div className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">Per-Axis Thresholds ({unitLabel})</div>
                        <div className="grid grid-cols-3 gap-1">
                          {(['V', 'H', 'A'] as const).map(a => {
                            const t = thrByAxis[a];
                            const active = visAxes.length === 1 && visAxes[0] === a;
                            return (
                              <div key={a} className="flex flex-col gap-0.5 rounded-md px-2 py-1.5 transition-colors"
                                style={{ opacity: hiddenAxes.has(a) ? 0.3 : 1, background: active ? `${AXIS_COLORS[a]}18` : 'transparent', border: active ? `1px solid ${AXIS_COLORS[a]}40` : '1px solid transparent' }}>
                                <div className="flex items-center gap-1 mb-0.5">
                                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: AXIS_COLORS[a] }} />
                                  <span className="text-xs font-semibold text-gray-700">{a}</span>
                                </div>
                                {t ? (
                                  <>
                                    <span className="text-xs tabular-nums" style={{ color: '#b45309' }}>3σ: {t.yellow.toFixed(decimals)}</span>
                                    <span className="text-xs tabular-nums text-red-600">Sev: {t.red.toFixed(decimals)}</span>
                                  </>
                                ) : <span className="text-xs text-gray-400">—</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">{axisLabel}Alarm Threshold (3σ)</span>
                        <span className="text-gray-900">{thresholds.yellow.toFixed(decimals)}</span>
                      </div>
                      <input type="range" min="0" max={Math.max(thresholds.red * 2, 1)} step={step}
                        value={thresholds.yellow}
                        onChange={(e) => handleThresholdChange(metricKey, 'yellow', Number(e.target.value))}
                        className="w-full h-2 bg-yellow-200 rounded-lg appearance-none cursor-grab active:cursor-grabbing slider" />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">{axisLabel}Critical Threshold (3σ ×1.5)</span>
                        <span className="text-gray-900">{thresholds.red.toFixed(decimals)}</span>
                      </div>
                      <input type="range" min="0" max={Math.max(thresholds.red * 2, 1)} step={step}
                        value={thresholds.red}
                        onChange={(e) => handleThresholdChange(metricKey, 'red', Number(e.target.value))}
                        className="w-full h-2 bg-red-200 rounded-lg appearance-none cursor-grab active:cursor-grabbing slider" />
                    </div>
                    {dirty && (
                      <>
                        <button onClick={() => handleSave(metricKey)} disabled={savingMetric === metricKey}
                          className="w-full py-2 rounded-lg text-sm font-medium transition-colors bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60">
                          {savingMetric === metricKey ? 'Saving…' : 'Save Thresholds'}
                        </button>
                        {saveError && savingMetric === null && <p className="text-xs text-red-500 mt-1">{saveError}</p>}
                      </>
                    )}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Temperature chart — separate small card */}
          {filteredTemp.length > 0 && (
            <ChartContainer title="Temperature (°F)">
              {(() => {
                const yMax = calcYMax(filteredTemp);
                return (
                  <div className="flex gap-2 items-start">
                    <FloorSlider floor={tempFloor} setFloor={setTempFloor} yMax={yMax} />
                    <div className="flex-1 min-w-0">
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={filteredTemp} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                          <YAxis unit="°F" domain={[0, yMax]} />
                          <Tooltip formatter={(v: number) => [`${v}°F`, 'Sensor Temp']} />
                          {tempFloor > 0 && <ReferenceLine y={tempFloor} stroke="#64748b" strokeDasharray="4 4" strokeWidth={1} label={{ value: `Floor: ${tempFloor.toFixed(1)}`, position: 'insideTopLeft', fill: '#64748b', fontSize: 10 }} />}
                          <Line type="monotone" dataKey="temp" stroke="#F97316" strokeWidth={2} isAnimationActive={false} connectNulls dot={{ r: 2 }} name="Sensor Temp" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })()}
            </ChartContainer>
          )}

          {/* Waveform Viewer */}
          {point.machineId && (
            <WaveformViewer
              machineId={point.machineId}
              jumpTo={waveformJumpTo}
              operatingRpm={null}
              isVfd={false}
            />
          )}
      </div>
    </div>
  );
}
